import { useCallback, useEffect, useRef, useState } from 'react';

// Browser Web Speech API (Chrome/Safari prefix it; Firefox has none). Undefined
// elsewhere → we degrade silently and the caller hides the mic button, per the
// product non-negotiable: "Voice input must gracefully degrade … fall back to
// text input silently." Read when used, not at load, so a page (or a test) that
// provides it later is seen.
function speechRecognitionImpl(): unknown {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
  return w.SpeechRecognition || w.webkitSpeechRecognition;
}

/** Why dictation stopped without words: the mic is blocked, nothing was heard, or it failed. */
export type VoiceError = 'blocked' | 'no_speech' | 'failed';

export interface UseVoiceInput {
  /** False when the browser has no SpeechRecognition — hide the mic button. */
  supported: boolean;
  recording: boolean;
  /** The words heard so far, before they are final (empty when the caller does not need them). */
  interim: string;
  /** Set when the last dictation ended on an error; cleared by the next start. */
  error: VoiceError | null;
  /** Start dictation (or stop it if already recording). */
  toggle: () => void;
  /** Stop listening; words already said still arrive. */
  stop: () => void;
  /** Stop listening and drop anything still on its way (the box was just sent). */
  cancel: () => void;
}

const ERRORS: Record<string, VoiceError> = {
  'not-allowed': 'blocked',
  'service-not-allowed': 'blocked',
  'audio-capture': 'blocked',
  'no-speech': 'no_speech',
};

/**
 * Thin wrapper over the Web Speech API for one-shot dictation. On a final result
 * it calls `onTranscript(text)` (the caller appends it to its input); the words
 * heard so far are in `interim`. No-ops when unsupported. The callback is read
 * through a ref so a re-render doesn't restart an in-flight recognition session.
 */
export function useVoiceInput(onTranscript: (text: string) => void): UseVoiceInput {
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<VoiceError | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);
  const cbRef = useRef(onTranscript);
  cbRef.current = onTranscript;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setRecording(false);
    setInterim('');
  }, []);

  const cancel = useCallback(() => {
    const recognition = recognitionRef.current;
    // Late results from this session are ignored once it is no longer the current one.
    recognitionRef.current = null;
    if (recognition) (recognition.abort ?? recognition.stop).call(recognition);
    setRecording(false);
    setInterim('');
  }, []);

  const toggle = useCallback(() => {
    const Impl = speechRecognitionImpl();
    if (!Impl) return;
    if (recognitionRef.current && recording) {
      recognitionRef.current.stop();
      setRecording(false);
      setInterim('');
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recognition = new (Impl as any)();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      if (recognitionRef.current !== recognition) return;
      let heard = '';
      const results = event.results ?? [];
      for (let i = event.resultIndex ?? 0; i < results.length; i++) {
        const transcript = String(results[i]?.[0]?.transcript ?? '').trim();
        if (!transcript) continue;
        if (results[i].isFinal) cbRef.current(transcript);
        else heard = heard ? `${heard} ${transcript}` : transcript;
      }
      setInterim(heard);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onerror = (event: any) => {
      if (recognitionRef.current !== recognition) return;
      // "aborted" is our own stop, not a failure.
      if (event?.error !== 'aborted') setError(ERRORS[String(event?.error)] ?? 'failed');
      setRecording(false);
      setInterim('');
    };
    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      setRecording(false);
      setInterim('');
    };
    setError(null);
    try {
      recognition.start();
    } catch {
      setError('failed');
      return;
    }
    recognitionRef.current = recognition;
    setRecording(true);
  }, [recording]);

  // Stop dictation if the component unmounts mid-recording.
  useEffect(() => () => recognitionRef.current?.stop(), []);

  return { supported: !!speechRecognitionImpl(), recording, interim, error, toggle, stop, cancel };
}
